import type { LocationQuery } from 'vue-router';

/**
 * An A/B experiment run on an Unleash flag. The flag's variants are the arms; the flag being on
 * for a user means they are in the experiment, and the variant Unleash answers names their arm.
 */
export interface ExperimentDefinition<Arm extends string = string> {
  /** The Unleash flag, kebab-case and ending in `-enabled` like every other flag. */
  flag: string;
  /** Every variant name the flag may answer with. Anything else is treated as control. */
  arms: readonly Arm[];
  /** The arm that matches the experience users outside the experiment get. */
  control: Arm;
}

export type ArmOf<D extends ExperimentDefinition> = D['arms'][number];

export const defineExperiment = <const Arm extends string>(
  experiment: ExperimentDefinition<Arm>,
): ExperimentDefinition<Arm> => experiment;

/** The query parameter that forces an arm for QA: `?experiment=<flag>:<arm>`, comma-separated for several. */
export const EXPERIMENT_OVERRIDE_QUERY_PARAM = 'experiment';

/**
 * The arm the URL asks this experiment to show, or null when it names none or names one the
 * experiment does not have. Callers only honour it behind the dev-mode gate.
 */
export const readExperimentOverride = <D extends ExperimentDefinition>({ query, experiment }: { query: LocationQuery; experiment: D }): ArmOf<D> | null => {
  const raw = query[EXPERIMENT_OVERRIDE_QUERY_PARAM];
  const values = (Array.isArray(raw) ? raw : [raw]).filter((value): value is string => typeof value === 'string');

  for (const value of values) {
    for (const pair of value.split(',')) {
      const [flag, arm] = pair.split(':');
      if (flag === experiment.flag && arm && experiment.arms.includes(arm)) {
        return arm as ArmOf<D>;
      }
    }
  }

  return null;
};
